import { useMemo } from 'react';

/**
 * Custom hook to extract current revision data and status
 * Memoizes computed values for performance
 * 
 * @param {Array} revisionData - Array of revision objects
 * @param {string} revisionId - The current revision ID
 * @returns {Object} Current revision and status information
 */
const useCurrentRevisionData = (revisionData, revisionId) => {
  // Find current revision based on revisionId
  const currentRevision = useMemo(() => {
    if (!revisionData || !revisionId) return null;
    
    return revisionData.find(
      (rev) => rev.procedure_revision_id === revisionId
    );
  }, [revisionData, revisionId]);

  // Get current status from the latest status change (normalized for pending approvals)
  const { rawStatus, currentStatus } = useMemo(() => {
    if (!currentRevision || !currentRevision.status_changes) {
      return { rawStatus: null, currentStatus: null };
    }

    const latestStatusChange = currentRevision.status_changes.find(
      (sc) => sc.is_latest
    );

    const statusValue = latestStatusChange?.status_changed_to || null;
    const hasPendingApprovals = currentRevision.required_by?.some(
      (req) => req.purpose?.toLowerCase() === "approve" && req.is_open
    );

    if (
      statusValue &&
      statusValue.toLowerCase() === "approved" &&
      hasPendingApprovals
    ) {
      return {
        rawStatus: statusValue,
        currentStatus: "Submitted For Approval",
      };
    }

    return { rawStatus: statusValue, currentStatus: statusValue };
  }, [currentRevision]);

  return {
    currentRevision,
    currentStatus,
    rawStatus,
  };
};

export default useCurrentRevisionData;

